/**
 * Validate a single Instagram username
 * Rules:
 * - Must be between 1 and 30 characters long
 * - Can only contain letters, numbers, periods (.), and underscores (_)
 */
export const INSTAGRAM_USERNAME_RULES = {
  minLength: 1,
  maxLength: 30,
  pattern: /^[a-zA-Z0-9._]+$/,
  description: "letters, numbers, periods (.), and underscores (_)"
} as const;

/**
 * UI-friendly validation for a single Instagram username.
 * Returns an empty string when valid; otherwise, an error message.
 * Use `allowEmpty: true` to suppress the empty error during initial input.
 */
export function getInstagramUsernameError(
  username: string,
  options?: { allowEmpty?: boolean }
): string {
  const trimmed = username?.trim() ?? "";

  if (!trimmed) {
    return options?.allowEmpty ? "" : "Username cannot be empty";
  }

  if (trimmed.length < INSTAGRAM_USERNAME_RULES.minLength || trimmed.length > INSTAGRAM_USERNAME_RULES.maxLength) {
    return `Username must be between ${INSTAGRAM_USERNAME_RULES.minLength} and ${INSTAGRAM_USERNAME_RULES.maxLength} characters long`;
  }

  if (!INSTAGRAM_USERNAME_RULES.pattern.test(trimmed)) {
    return `Username can only contain ${INSTAGRAM_USERNAME_RULES.description}`;
  }

  return "";
}

export function validateInstagramUsername(username: string): { isValid: boolean; error: string } {
  const error = getInstagramUsernameError(username, { allowEmpty: false });
  if (error) {
    // Keep detailed messages (including the provided username) for request-level validation
    const trimmed = username?.trim() ?? "";
    if (!trimmed) return { isValid: false, error: "Username cannot be empty" };
    if (trimmed.length < INSTAGRAM_USERNAME_RULES.minLength || trimmed.length > INSTAGRAM_USERNAME_RULES.maxLength) {
      return { isValid: false, error: `Username '${trimmed}' must be between ${INSTAGRAM_USERNAME_RULES.minLength} and ${INSTAGRAM_USERNAME_RULES.maxLength} characters long` };
    }
    return { isValid: false, error: `Username '${trimmed}' can only contain ${INSTAGRAM_USERNAME_RULES.description}` };
  }
  return { isValid: true, error: "" };
}

/**
 * Validate comma-separated Instagram usernames
 */
export function validateInstagramUsernames(usernamesString: string): { 
  isValid: boolean; 
  error: string; 
  usernames: string[] 
} {
  if (!usernamesString || !usernamesString.trim()) {
    return { isValid: false, error: "At least one username is required", usernames: [] };
  }

  // Parse and clean usernames
  const usernames = usernamesString
    .split(',')
    .map(u => u.trim())
    .filter(u => u.length > 0);

  if (usernames.length === 0) {
    return { isValid: false, error: "At least one valid username is required", usernames: [] };
  }

  // Validate each username
  for (const username of usernames) {
    const { isValid, error } = validateInstagramUsername(username);
    if (!isValid) {
      return { isValid: false, error, usernames: [] };
    }
  }

  return { isValid: true, error: "", usernames };
} 

// Days validation for analytics windows
export const ANALYTICS_DAYS_RULES = {
  min: 1,
  max: 60
} as const;

/**
 * UI-friendly validation for the number of days.
 * Returns an empty string when valid; otherwise, an error message.
 * Use `allowZeroAsEmpty: true` to suppress errors when the input is blank (mapped to 0 in the UI).
 */
export function getAnalyticsDaysError(
  days: number,
  options?: { allowZeroAsEmpty?: boolean }
): string {
  if (options?.allowZeroAsEmpty && (days ?? 0) <= 0) {
    return "";
  }
  if (!Number.isInteger(days) || days < ANALYTICS_DAYS_RULES.min) {
    return "Days must be a positive integer";
  }
  if (days > ANALYTICS_DAYS_RULES.max) {
    return `Days cannot exceed ${ANALYTICS_DAYS_RULES.max}`;
  }
  return "";
}

export function validateAnalyticsDays(days: number): { isValid: boolean; error: string } {
  const error = getAnalyticsDaysError(days, { allowZeroAsEmpty: false });
  return { isValid: !Boolean(error), error };
}